import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * ROI 2026-08-03 — watches the summary for late height changes.
   *
   * The sticky/non-sticky decision is a one-shot measurement taken when the
   * drawer opens. But the summary is mostly APP-INJECTED and lands late:
   * Redo's Checkout+ row, Shop Pay installments, and the Shop / PayPal /
   * GPay wallet buttons all render after open. At measure time the summary
   * is short, the ratio comes in under the threshold, and the drawer commits
   * to sticky mode — then the summary balloons to well over half the drawer
   * and permanently squats there, crushing the item list to a single clipped
   * row with no way to see the rest. That is CJ's phone; a dev viewport
   * without those apps never reproduces it, which is why this took a while.
   *
   * Re-measuring whenever the summary resizes lets the drawer correct itself
   * to the single-scroll layout the moment the wallets land.
   *
   * @type {ResizeObserver | null}
   */
  #summaryObserver = null;

  /** Last value written, so re-measuring can't thrash the attribute. */
  #lastSticky = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = null;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#observeSummary();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * (Re)attaches the summary resize watcher — see #summaryObserver.
   * Re-queried on every open because cart re-renders replace the summary node.
   */
  #observeSummary() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#summaryObserver?.disconnect();

    const summary = this.#dialog?.querySelector('.cart-drawer__summary');
    if (!summary) return;

    this.#summaryObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.#updateStickyState());
    });
    this.#summaryObserver.observe(summary);
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      this.#setSticky(dialog, 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // Measure the summary's NATURAL height, not its rendered box. In sticky
    // mode the CSS caps the summary at 50% of the drawer, so the rendered
    // height can never exceed the threshold and the sticky decision could
    // never flip back — the resize re-measure existed precisely to catch a
    // summary that outgrew the drawer, and the cap was blinding it.
    // scrollHeight sees through the cap; max() keeps the uncapped case exact.
    const summaryHeight = Math.max(summary.scrollHeight, summary.getBoundingClientRect().height);
    const ratio = summaryHeight / drawerHeight;
    this.#setSticky(dialog, ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Writes the sticky attribute only on an actual change. The resize watcher
   * re-enters this on every summary height change, and flipping the attribute
   * restyles the summary — so writing unconditionally risks a feedback loop.
   *
   * @param {HTMLDialogElement} dialog
   * @param {'true' | 'false'} value
   */
  #setSticky(dialog, value) {
    if (this.#lastSticky === value && dialog.getAttribute('cart-summary-sticky') === value) return;
    this.#lastSticky = value;
    dialog.setAttribute('cart-summary-sticky', value);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
